import { Box, Grid2 as Grid, Stack } from "@mui/material"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useParams } from "react-router-dom"
import { useAuth } from "../../auth"
import { DataField, DataRow } from "../../components/DataField"
import DeleteBar from "../../components/DeleteBar"
import DialogConfirm from "../../components/DialogConfirm"
import DialogForm from "../../components/DialogForm"
import RecordInfo from "../../components/RecordInfo"
import { SectionTitle } from "../../components/SectionTitle"
import AssemblaggioForm from "../../components/forms/AssemblaggioForm"
import {
  useAssemblaggio,
  useAssemblaggioDelete,
  useAssemblaggioHistory,
  useAssemblaggioUpdate,
} from "../../hooks/assemblaggi"
import { useDesiredHeight } from "../../hooks/measuring"
import { useIsMobile } from "../../hooks/mobile"
import useUserCanWriteAndDelete from "../../hooks/permissions"
import { useSnackbarMessage } from "../../hooks/snackbar"
import { Assemblaggio } from "../../types"

export default function AssemblaggioDettaglio() {
  const { id } = useParams()
  const { t, i18n } = useTranslation()
  const { data: assemblaggio, refetch } = useAssemblaggio(parseInt(id as string))
  const { mutateAsync: update } = useAssemblaggioUpdate()
  const { mutateAsync: remove } = useAssemblaggioDelete()
  const userCanWriteAndDelete = useUserCanWriteAndDelete()
  const isMobile = useIsMobile()
  const showMessage = useSnackbarMessage()
  const navigate = useNavigate()
  const { user } = useAuth()
  const lang = i18n.language
  const note = i18n.language === "en" ? assemblaggio?.note_en : assemblaggio?.note_it

  const [updateModalState, updateModalActions] = useModalTrigger()
  const [deleteModalState, deleteModalActions] = useModalTrigger()
  const historyBag = useAssemblaggioHistory(id as string)
  const { ref } = useDesiredHeight(24)

  useEffect(() => {
    if (user?.ruolo === "consultazione") {
      navigate(`/${lang}/fondi`)
    }
  }, [user, lang, navigate])

  return (
    <>
      <Grid container spacing={2} sx={{ overflow: "auto", height: "100%", p: 3, pt: 2 }}>
        <Grid size={{ xs: 12, xl: 12 }}>
          <Stack flexGrow={1} sx={{ height: "fit-content", position: "relative", bgcolor: "white", boxShadow: 1 }}>
            <SectionTitle first title={t("general.dettaglio")} onEdit={() => updateModalActions.open()} />
            <Box px={3} pb={2}>
              <DataRow>
                <DataField label={t("assemblaggio.nome")} value={assemblaggio?.nome} size={12} />
              </DataRow>
              <DataRow last>
                <DataField label={t("assemblaggio.note")} value={note} size={12} />
              </DataRow>
            </Box>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, xl: 12 }}>
          <Stack ref={ref} sx={{ bgcolor: "grey.200", borderRadius: 1, boxShadow: 1 }}>
            <SectionTitle bgcolor="grey.700" info />
            <Stack sx={{ p: 2 }} flexGrow={1}>
              <RecordInfo
                first_update={assemblaggio?.first_update}
                last_update={assemblaggio?.last_update}
                historyBag={historyBag as any}
              />
            </Stack>
            {userCanWriteAndDelete && !isMobile && assemblaggio?.can_delete && (
              <DeleteBar title={t("assemblaggio.cta_elimina")} onDelete={() => deleteModalActions.open(assemblaggio)} />
            )}
          </Stack>
        </Grid>
      </Grid>

      <DialogForm<Assemblaggio>
        open={updateModalState.isOpen}
        handleClose={updateModalActions.close}
        FormComponent={AssemblaggioForm}
        title={t("assemblaggio.cta_modifica")}
        item={assemblaggio}
        hasCancel
        hasDoubleLanguage
        save={(values) =>
          update(values).then(() => {
            updateModalActions.close()
            refetch()
            showMessage(t("assemblaggio.esito_modifica_ok"))
          })
        }
      />
      <DialogConfirm
        open={deleteModalState.isOpen}
        handleClose={deleteModalActions.close}
        title={t("assemblaggio.cta_elimina")}
        subtitle={
          <span>
            {t("assemblaggio.cta_subtitle_elimina")} <b>"{deleteModalState.value?.nome}"</b>&nbsp;?
          </span>
        }
        textConfirm={t("general.elimina")}
        onConfirm={() =>
          remove(deleteModalState.value.id)
            .then(() => {
              deleteModalActions.close()
              showMessage(t("assemblaggio.esito_elimina_ok"))
              navigate(`/${i18n.language}/assemblaggi`)
            })
            .catch(() => {
              deleteModalActions.close()
              showMessage(t("general.esito_elimina_errore"), "error")
            })
        }
      />
    </>
  )
}
